import * as React from 'react';
import { Popover, PopoverBody } from 'reactstrap';
import { AvailableFilters, SelectedFilter } from '.';
import { FilterEdit } from '.';
import { EntityDefinition } from 'types';
import { FilterState, FilterChangeLocation } from '../ListingContainer';
import { FocusScope } from 'react-aria';
/**
 * Definition for local props. Most state is handled by `ListingSearch` and passed down to us via
 * the `selectedFilters` prop.
 *
 * @interface FilterProps
 */
interface FilterProps {
	entityDefinition: EntityDefinition;
	selectedFilters: SelectedFilter[];
	filters: FilterState;
}

/**
 * Component to manage a list of filters for the current listing page
 */
class Filters extends React.Component<FilterProps> {
	private filterButtonWrapper: React.RefObject<HTMLDivElement>;
	private filterButton: React.RefObject<HTMLButtonElement>;

	constructor(props) {
		super(props);

		this.filterButtonWrapper = React.createRef();
		this.filterButton = React.createRef();

		if (this.props.entityDefinition.filters.length === 0) {
			console.warn('No filters defined.');
		}
	}

	public handlePopoverClose = () => {
		this.props.filters.toggleFilterList();

		if (!this.filterButton || !this.filterButton.current) {
			return;
		}

		this.filterButton.current.focus();
	};

	public render() {
		const {
			entityDefinition,
			selectedFilters,
			filters: {
				onFilterRemoved,
				toggleFilterChange,
				displayFilterList,
				closeFilterList,
				toggleFilterList,
				filterBeingChanged,
				filterChangeLocation,
				onFilterChange,
				cancelFilterChange,
				beginFilterChange,
			},
		} = this.props;

		// if there aren't any available filters, let's just not render anything
		if (entityDefinition.filters.length === 0) {
			return null;
		}

		return (
			<div ref={this.filterButtonWrapper}>
				<button
					id="FiltersPopoverToggle"
					data-testid="toggle-filters-button"
					className="dropdown-toggle listing__filter-dropdown btn-medium btn btn-outline-primary"
					onClick={() => {
						toggleFilterList();
					}}
					ref={this.filterButton}
					type="button"
					aria-expanded={displayFilterList}
					aria-haspopup="dialog"
				>
					<span className="dropdown-toggle__text">Filter</span>
				</button>

				{filterBeingChanged !== null && filterChangeLocation === FilterChangeLocation.SelectedFilter ? (
					<Popover
						className="filters-popover filters-popover--edit"
						placement="bottom"
						isOpen={true}
						target={`Filters_${filterBeingChanged.name}_Popover`}
						role="dialog"
						aria-label={`${filterBeingChanged.displayName} filter`}
						toggle={toggleFilterChange}
					>
						<FocusScope autoFocus={false} contain={true} restoreFocus={true}>
							<PopoverBody className="filters-popover__body">
								<FilterEdit
									selectedFilter={filterBeingChanged}
									onFilterChange={onFilterChange}
									onFilterRemoved={onFilterRemoved}
									cancelFilterChange={cancelFilterChange}
									closeList={closeFilterList}
									handlePopoverClose={toggleFilterChange}
								/>
							</PopoverBody>
						</FocusScope>
					</Popover>
				) : null}

				<Popover
					className="filters-popover"
					placement={entityDefinition.filterPopoverPlacement}
					isOpen={displayFilterList}
					target="FiltersPopoverToggle"
					role="dialog"
					aria-label="Filters"
					toggle={toggleFilterList}
				>
					<FocusScope autoFocus={false} contain={true} restoreFocus={true}>
						<PopoverBody className="filters-popover__body">
							{filterBeingChanged === null ? (
								<>
									<AvailableFilters
										filters={entityDefinition.filters}
										selectedFilters={selectedFilters}
										beginFilterChange={beginFilterChange}
										handlePopoverClose={this.handlePopoverClose}
									/>
								</>
							) : filterChangeLocation === FilterChangeLocation.FilterList ? (
								<>
									<FilterEdit
										selectedFilter={filterBeingChanged}
										onFilterChange={onFilterChange}
										onFilterRemoved={onFilterRemoved}
										cancelFilterChange={cancelFilterChange}
										closeList={closeFilterList}
										handlePopoverClose={this.handlePopoverClose}
									/>
								</>
							) : null}
						</PopoverBody>
					</FocusScope>
				</Popover>
			</div>
		);
	}
}

export default Filters;
